import type { StateCreator } from 'zustand';
import type { IAgent } from '@/lib/api/agents.client';
import { createSession, previewGuidedPreset } from '@/lib/api/sessions.client';
import type { IChat } from '@/lib/types/chat';
import { useBudgetStore } from '@/lib/stores/budget.store';
import { ownListsWith } from '@/lib/stores/chat-list-rows';
import {
  agentStartingModel,
  fallbackModel,
  storedCatalog,
} from '@/lib/stores/chat-store/chat-model';
import {
  boundDraftsByThread,
  createDraftChat,
  draftModelSelection,
  isBoundChat,
  loadGreeting,
  PERSISTED_SESSION_ID,
  promotedPlainDraft,
  rememberPromotedDraft,
} from '@/lib/stores/chat-store/drafts';
import { captureWorkspace, type SameWorkspace } from '@/lib/stores/chat-store/workspace-generation';
import type { ChatState, GetChatState, SetChatState } from '@/lib/stores/chat.store';
import { sessionToHistory } from '@/lib/stores/session-history';
import { useSessionStore } from '@/lib/stores/session.store';
import {
  draftMatchesScope,
  scopeNeedsPreparedDraft,
  type DraftScope,
} from '@/lib/utils/new-chat-route';

/** What a new agent chat needs to know about its agent up front. */
export type AgentDraftSource = Pick<
  IAgent,
  | 'id'
  | 'name'
  | 'startMessage'
  | 'isPaused'
  | 'pauseReason'
  | 'ownerUserId'
  | 'createdBy'
  | 'defaultModel'
  | 'defaultModelTier'
  | 'allowedModels'
>;

/**
 * New chats of the chat store (KAN-1266): opening a draft, preparing one for
 * an agent, a project or a guided preset, and turning it into a session when
 * its first message goes out.
 */
interface DraftsData {
  /**
   * A draft set up by a "new chat" entry inside an agent or project, waiting
   * for the embedded ``…/chat/new`` address to pick it up (``createNewChat``
   * with a matching scope). Nothing is created on the server until the first
   * message is sent.
   */
  preparedDraft: IChat | null;
  pendingSessionPromise: Promise<string> | null;
}

export interface DraftsSlice extends DraftsData {
  /**
   * Open a draft as the current chat. With a ``scope`` (an embedded
   * ``…/chat/new`` address) it takes the prepared draft of that agent or
   * project; the caller checks ``preparedDraft`` first.
   */
  createNewChat: (id?: string, scope?: DraftScope) => Promise<void> | void;
  /**
   * Set up a new chat with an agent: bound to it, opening on its greeting (or
   * on ``startMessage`` in its place) and on its model.
   */
  prepareAgentDraft: (agent: AgentDraftSource, startMessage?: string) => void;
  /**
   * Set up a new chat inside a project. It starts private; the panel's
   * visibility switch shares it, before the first message too.
   */
  prepareProjectDraft: (projectId: string) => void;
  /**
   * Set up a guided chat (skill builder, app editor, …) on the greeting and
   * model the API previews for it. Rejects where creating it would, so the
   * entry point can say so on the click. Nothing is created yet.
   */
  prepareGuidedDraft: (preset: string, context?: Record<string, unknown>) => Promise<void>;
  // Resolve the backend session for the chat the caller is bound to, creating
  // it if that chat is still a draft. ``boundChatId`` is the id the caller's
  // composer renders (draft thread id or session id); pass it so a
  // ``currentChat`` that drifted to another chat can never be adopted — see
  // the drift guard in the implementation (KAN-315).
  ensureSessionForCurrentChat: (boundChatId?: string) => Promise<string>;
}

/** The slice's state with no draft around, at start and after a reset. */
export const initialDraftsState = (): DraftsData => ({
  preparedDraft: null,
  pendingSessionPromise: null,
});

// The model a new chat starts on, for the budget the user is in right now
// (``fallbackModel``); the selection while neither catalog nor default is known.
const startingModel = (state: ChatState): string =>
  fallbackModel(storedCatalog(state), useBudgetStore.getState().lowBudgetMode) ||
  state.selectedModel;

const createNewChat = async (
  set: SetChatState,
  get: GetChatState,
  id?: string,
  scope?: DraftScope
) => {
  if (scope && scopeNeedsPreparedDraft(scope)) {
    const prepared = get().preparedDraft;
    if (!prepared || !draftMatchesScope(prepared, scope)) return;
    const draft = id ? { ...prepared, id } : prepared;
    boundDraftsByThread.set(draft.id, draft);
    set({
      currentChat: draft,
      activeChatId: draft.id,
      preparedDraft: null,
      // The agent's or preset's model, the way opening one of its chats restores it.
      ...draftModelSelection(draft),
    });
    return;
  }
  // The runtime can hand out a thread id again that an abandoned agent or
  // project draft had; that binding must not outlive it.
  if (id) boundDraftsByThread.delete(id);
  // Or one whose upload already created its session: it opens as that chat,
  // so its first message goes there and no second session is created (KAN-1825).
  const promoted = id ? promotedPlainDraft(id) : undefined;
  if (promoted) {
    set({ currentChat: promoted, activeChatId: promoted.id });
    return;
  }
  const draft = createDraftChat(startingModel(get()));
  const nextDraft = id ? { ...draft, id } : draft;
  set({ currentChat: nextDraft, activeChatId: nextDraft.id });
};

const prepareAgentDraft = (
  set: SetChatState,
  get: GetChatState,
  agent: AgentDraftSource,
  startMessage?: string
) => {
  const state = get();
  const draft = createDraftChat(
    agentStartingModel(agent, state.recommendedModels, state.modelPreference) ??
      startingModel(state)
  );
  const myUserId = useSessionStore.getState().profile?.sub ?? null;
  set({
    preparedDraft: {
      ...draft,
      draftGreeting: (startMessage ?? agent.startMessage) || undefined,
      agentId: agent.id,
      agentName: agent.name,
      agentIsPaused: agent.isPaused,
      agentPauseReason: agent.pauseReason,
      // Owner only, the same rule the API applies to a created session.
      agentCanEdit: !!myUserId && (agent.ownerUserId ?? agent.createdBy) === myUserId,
      ownerUserId: myUserId,
      draftStartMessage: startMessage,
    },
    ...draftModelSelection(draft),
  });
};

const prepareProjectDraft = (set: SetChatState, get: GetChatState, projectId: string) => {
  const draft = createDraftChat(startingModel(get()));
  set({
    preparedDraft: {
      ...draft,
      projectId,
      projectShared: false,
      ownerUserId: useSessionStore.getState().profile?.sub ?? null,
    },
    ...draftModelSelection(draft),
  });
};

const prepareGuidedDraft = async (
  set: SetChatState,
  get: GetChatState,
  preset: string,
  context?: Record<string, unknown>
) => {
  const preview = await previewGuidedPreset(preset, context);
  const draft = createDraftChat(preview.modelId ?? startingModel(get()));
  set({
    preparedDraft: {
      ...draft,
      draftGreeting: preview.startMessage || undefined,
      guidedPreset: preset,
      guidedContext: context,
      ownerUserId: useSessionStore.getState().profile?.sub ?? null,
    },
    ...draftModelSelection(draft),
  });
};

/**
 * Create the session for the draft ``currentChat`` and promote the draft to
 * it. Resolves with the new session id.
 */
const promoteDraft = async (
  set: SetChatState,
  currentChat: IChat,
  sameWorkspace: SameWorkspace
): Promise<string> => {
  // No title argument → the backend localizes the placeholder to the
  // user's UI locale. A user-renamed draft (rare pre-send) keeps its title.
  // An agent, project or guided draft is created with its binding; the API
  // writes the greeting as the first message, the same text the draft shows.
  const created = await createSession(
    currentChat.title || undefined,
    currentChat.agentId ?? undefined,
    currentChat.guidedPreset,
    currentChat.guidedContext,
    currentChat.projectId ?? undefined,
    currentChat.projectShared,
    currentChat.draftStartMessage
  );
  const draftId = currentChat.id;
  const persisted: IChat = {
    ...currentChat,
    id: created.id,
    title: created.title ?? currentChat.title,
    createdAt: created.createdAt ? new Date(created.createdAt) : new Date(),
    updatedAt: created.updatedAt ? new Date(created.updatedAt) : new Date(),
    isDraft: false,
    messages: currentChat.draftGreeting
      ? await loadGreeting(created.id, currentChat.draftGreeting, currentChat.model)
      : currentChat.messages,
    draftGreeting: undefined,
    draftStartMessage: undefined,
    promotedFromDraftId: draftId,
  };
  boundDraftsByThread.delete(draftId);
  // The tab switched workspace meanwhile: the session belongs to the one it
  // left, and nothing of it goes into this one. The caller still gets its id.
  if (!sameWorkspace()) return created.id;
  rememberPromotedDraft(draftId, persisted);
  set((state) => ({
    // Only promote the draft to "the chat on screen" while it still IS
    // the chat on screen. Session creation can take seconds under load,
    // and a user who navigated away in the meantime must not be yanked
    // into the chat they just left (same class of bug as KAN-315). The
    // list entry below is added either way, so the new chat shows up in
    // its list regardless: the plain sidebar for a plain chat, the
    // agent's list for an agent chat, the project's own lists (they
    // follow the realtime events) for a project chat.
    ...(state.currentChat?.id === draftId
      ? { currentChat: persisted, activeChatId: persisted.id }
      : {}),
    ...ownListsWith(state, sessionToHistory(created), persisted),
    pendingSessionPromise: null,
  }));
  return created.id;
};

const ensureSessionForCurrentChat = async (
  set: SetChatState,
  get: GetChatState,
  boundChatId?: string
) => {
  // Drift guard (KAN-315): the caller tells us which chat its composer is
  // bound to. If ``currentChat`` points somewhere else — a background
  // stream finishing in another chat used to move it — resolving
  // ``currentChat.id`` would post the message into that foreign session.
  // Trust the caller instead: an already-persisted chat resolves to
  // itself, a draft thread gets its own fresh session below.
  if (boundChatId && !isBoundChat(get().currentChat, boundChatId)) {
    if (PERSISTED_SESSION_ID.test(boundChatId)) return boundChatId;
    set({
      currentChat: boundDraftsByThread.get(boundChatId) ?? {
        ...createDraftChat(startingModel(get())),
        id: boundChatId,
      },
      activeChatId: boundChatId,
      // Any creation in flight belongs to the chat we just left — never
      // hand its session to this composer.
      pendingSessionPromise: null,
    });
  }

  const { currentChat, pendingSessionPromise } = get();
  if (!currentChat) throw new Error('Kein aktiver Chat verfügbar');
  if (!currentChat.isDraft) return currentChat.id;
  if (pendingSessionPromise) return pendingSessionPromise;

  const sameWorkspace = captureWorkspace();
  const promise = promoteDraft(set, currentChat, sameWorkspace).catch((error) => {
    if (sameWorkspace()) set({ pendingSessionPromise: null });
    throw error;
  });

  set({ pendingSessionPromise: promise });
  return promise;
};

export const createDraftsSlice: StateCreator<ChatState, [], [], DraftsSlice> = (set, get) => ({
  ...initialDraftsState(),
  createNewChat: (id, scope) => createNewChat(set, get, id, scope),
  prepareAgentDraft: (agent, startMessage) => prepareAgentDraft(set, get, agent, startMessage),
  prepareProjectDraft: (projectId) => prepareProjectDraft(set, get, projectId),
  prepareGuidedDraft: (preset, context) => prepareGuidedDraft(set, get, preset, context),
  ensureSessionForCurrentChat: (boundChatId) => ensureSessionForCurrentChat(set, get, boundChatId),
});
